import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LogoModel, LogoPosition, LogoSettings, logoSettingsSchema, LogoSettingsViewModel } from './logos-models';
import { Dialog, Form, PanelGroup, FormContext, DynamicContainer, OverlayTrigger } from '@hai/ui-react';
import { constant } from 'src/js/constant';
import { FormBuilder, FormFieldType, FormLayout } from 'src/js/component/form-builder';
import { getInterfaceLabelWithDigit, logoStore, updateLogoConfiguration } from './logos-store';
import { OneLogo } from './one-logo';
import { FormikProps } from 'formik';
import { EmptyThumbnail } from './empty-thumbnail';
import { FormSlider } from 'src/js/component/base/form/form-slider';
import { submitAndValidate } from 'src/js/validator/validator';
import { scrollIntoView } from 'src/js/util/global-util';
import { ButtonStateType } from '@hai/ui-react/dist/types';
import { GridManager, GridManagerRef } from 'src/js/component/grid-manager';
import { FormCheckbox } from 'src/js/component/base/form/form-checkbox';
interface Props {
  model: LogoSettingsViewModel;
  onSubmit: () => void;
  onCancel: () => void;
}

export const LogoConfigureModal = (props: Props) => {
  const { t } = useTranslation();
  const { model, onSubmit, onCancel, ...rest } = props;
  const formRef = useRef<FormikProps<LogoSettingsViewModel>>();
  const [anyChanges, setAnyChanges] = useState(false);
  const [buttonState, setButtonState] = useState<ButtonStateType>(undefined);

  const modal = React.useRef<HTMLDivElement>();
  const gridManagerRef = useRef<GridManagerRef<LogoModel>>();

  const [imageSelected, setImageSelected] = useState(model.fileName);
  const [newScrollTrigger, setNewScrollTrigger] = useState(0);
  const onLocalSelect = (model: LogoModel, isSelected: boolean) => {
    if (model == null) {
      /*eslint no-console: 0*/
      // console.warn('onLocalSelect null model');
      // normal if we press down when already at the end, just ignore
      return;
    }
    if (model.id === 0) {
      // no logo
      setImageSelected(isSelected ? '' : null);
      formRef.current.setFieldValue('fileName', '');
      return;
    }
    setImageSelected(isSelected ? model.name : null);
    formRef.current.setFieldValue('fileName', isSelected ? model.name : '');
  };
  const onLocalSubmit = (values: LogoSettingsViewModel) => {
    setButtonState('pending');
    // TODO a mapper from viewmodel => model would be good here
    return updateLogoConfiguration(values as LogoSettings, t).then((res) => {
      setButtonState(undefined);
      onSubmit();
      return res;
    });
  };

  let endrun = false;
  useEffect(() => {
    return () => {
      endrun = true;
    };
  }, []);

  const onKeyUpForm = (e: UIEvent & React.KeyboardEvent<HTMLElement>): void | boolean => {
    if ((e as any).srcElement?.className?.toString().includes('dropdown')) {
      // from dropdown
      return false;
    }

    if (e.key === 'Enter') {
      // console.log('enter from submit button', (e.target as HTMLElement)?.id?.toString(), formRef.current?.isSubmitting);
      if (formRef.current?.isSubmitting || (e.target as HTMLElement)?.id?.toString().includes('-submit')) {
        // from submit button, dont submit twice NKE-3307
        return;
      }
      submitAndValidate(formRef.current, t).then(() => {
        if (formRef.current?.isValid) {
          // success
          onCancel(); // close dialog
        }
      });
      return;
    }

    if (e.key === 'ESC') {
      onCancel(); // close dialog
      e.preventDefault();
      return;
    }

    if ((e as any).srcElement?.className?.toString().includes('HaiFormControl')) {
      // from form element
      return false; // ignore for Up/Down/Left/Right arrow
    }
  };

  let triggerMoveTimeout: any = null;
  const onKeyPressForm = (e: UIEvent & React.KeyboardEvent<HTMLElement>): void | boolean => {
    // console.log('keydown', e.key);
    if ((e as any).srcElement?.className?.toString().includes('dropdown')) {
      // from dropdown
      return false;
    }

    if (e.key === 'Enter') {
      return;
    }

    if (e.key === 'ESC') {
      return;
    }

    if (e.key === 'Tab') {
      return; // this allow normal key navigation between fields without our forced scrolling
    }

    if ((e as any).srcElement?.className?.toString().includes('HaiFormControl')) {
      // from form element
      return false; // ignore for Up/Down/Left/Right arrow
    }

    if (e.key === 'ArrowUp') {
      onLocalSelect(gridManagerRef.current?.onKeyUp(), true);
    }
    if (e.key === 'ArrowDown') {
      onLocalSelect(gridManagerRef.current?.onKeyDown(), true);
    }
    if (e.key === 'ArrowLeft') {
      onLocalSelect(gridManagerRef.current?.onKeyLeft(), true);
    }
    if (e.key === 'ArrowRight') {
      onLocalSelect(gridManagerRef.current?.onKeyRight(), true);
    }

    const triggerMove = () => {
      if (endrun) {
        triggerMoveTimeout = null;
        return;
      }
      // console.log('triggerMove', e.key);

      setNewScrollTrigger((v) => v + 1);
      triggerMoveTimeout = null;
    }; // end triggerMove

    if (triggerMoveTimeout === null) {
      triggerMoveTimeout = setTimeout(triggerMove, 10); // debounce
    }

    if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      // prevent bubble up and causing focus to go outside on other fields
      e.preventDefault();
      return false;
    }
  };

  useEffect(() => {
    if (formRef.current?.values.fileName === '') {
      scrollIntoView(`logo-no-logo`);
    } else if (formRef.current?.values.fileName) {
      scrollIntoView(`logo-img-${formRef.current?.values.fileName}`);
    }
  }, [newScrollTrigger]);

  useLayoutEffect(() => {
    if (model.fileName) {
      scrollIntoView(`logo-img-${model.fileName}`);
    }

    // adding watch event
    document.addEventListener('keyup', onKeyUpForm as EventListener);
    document.addEventListener('keydown', onKeyPressForm as EventListener);
    return () => {
      document.removeEventListener('keyup', onKeyUpForm as EventListener);
      document.removeEventListener('keydown', onKeyPressForm as EventListener);
    };
  }, []);

  const content = () => {
    return (
      <>
        <div id="logo-pickup-list" className="scrollable" ref={modal}>
          <div className="selectModal">
            <PanelGroup>
              <GridManager ref={gridManagerRef}>
                {(gridManager) => {
                  return (
                    <>
                      <OneLogo
                        gridManager={gridManager}
                        key={`nologo`}
                        id={`logo-no-logo`}
                        model={{
                          id: 0,
                          name: t('logos.noLogo'),
                          thumbnail: <EmptyThumbnail />,
                          resolution: null,
                          mtime: 0,
                        }}
                        selected={imageSelected == null || imageSelected === ''}
                        onSelect={(model: LogoModel, isSelected: boolean) => {
                          const zeroSelect = { ...model };
                          zeroSelect.name = '';
                          onLocalSelect(zeroSelect, isSelected);
                        }}
                      />
                      {logoStore &&
                        logoStore.map((value, index: number) => (
                          <OneLogo
                            gridManager={gridManager}
                            id={`logo-img-${value.name}`}
                            key={`${value.name}-${index}`}
                            model={value}
                            selected={value?.name === imageSelected}
                            onSelect={onLocalSelect}
                          />
                        ))}
                    </>
                  );
                }}
              </GridManager>
            </PanelGroup>
          </div>
        </div>
        <Form
          initialValues={model}
          defaultValidation={true}
          handleSubmit={onLocalSubmit}
          className="hai-mt-5"
          restValidationProps={{
            enableReinitialize: false, // "false" requires manual resetForm() above. We can then do partial update of the form while it's opened.
            validationSchema: logoSettingsSchema(t),
            innerRef: formRef,
          }}
          {...rest}
        >
          <FormContext.Consumer>
            {(formContext: FormikProps<LogoSettingsViewModel>) => {
              const values = formContext.values;
              const hasAnyChanges = formContext.dirty == true;
              if (hasAnyChanges !== anyChanges) {
                setAnyChanges(hasAnyChanges);
              }
              return (
                <>
                  <FormBuilder
                    translationPrefixLabels="logos.settings"
                    translationPrefixEnums="logos.enums"
                    layout={FormLayout.TWO_COLUMNS_FLEX}
                    fields={[
                      {
                        type: FormFieldType.DROPDOWN,
                        name: 'positioning',
                        enum: LogoPosition,
                      },
                      {
                        type: FormFieldType.NODE,
                        node: (
                          <div key="logo-settings-xy" className="mk-logo-settings-xy-fields">
                            <FormBuilder
                              translationPrefixLabels="logos.settings"
                              translationPrefixEnums="logos.enums"
                              layout={FormLayout.PLAIN_FIELDS}
                              fields={[
                                {
                                  type: FormFieldType.INPUT_NUMBER,
                                  name: 'x',
                                  disabled:
                                    values.positioning !== LogoPosition.LOGO_POS_ABSOLUTE &&
                                    values.positioning !== LogoPosition.LOGO_POS_RELATIVE,
                                  className: 'mk-input-med',
                                },
                                {
                                  type: FormFieldType.INPUT_NUMBER,
                                  name: 'y',
                                  disabled:
                                    values.positioning !== LogoPosition.LOGO_POS_ABSOLUTE &&
                                    values.positioning !== LogoPosition.LOGO_POS_RELATIVE,
                                  className: 'mk-input-med',
                                },
                              ]}
                            />
                          </div>
                        ),
                      },
                    ]}
                  />
                  <div className="logo-sliders">
                    <DynamicContainer minColumns={2} maxColumns={2}>
                      <FormSlider
                        title={t('logos.settings.scaling')}
                        name="scaling"
                        min="25"
                        max="400"
                        step="1"
                        showRange={{ left: '25%', right: '400%' }}
                        withValueInput
                        valueInputStyle={{ width: '51px' }}
                        inputValueSuffix={<span className="gray">&nbsp;%</span>}
                      />
                      <FormSlider
                        title={t('logos.settings.opacity')}
                        name="opacity"
                        min="0"
                        max="100"
                        step="1"
                        showRange={{ left: '0%', right: '100%' }}
                        withValueInput
                        valueInputStyle={{ width: '51px' }}
                        inputValueSuffix={<span className="gray">&nbsp;%</span>}
                      />
                    </DynamicContainer>
                  </div>
                  <div className="mt-3">
                    <OverlayTrigger
                      popoverContent={t('logos.settings.relativeTooltip')}
                      popoverBgColor={constant.productColor}
                      placement="top"
                      delay="1000"
                    >
                      <FormCheckbox name="relative" label={t('logos.settings.relative')} container={false} />
                    </OverlayTrigger>
                  </div>
                </>
              );
            }}
          </FormContext.Consumer>
        </Form>
      </>
    );
  };

  return (
    <Dialog
      accentColor={constant.productColor}
      title={t('logos.configure.title', { name: getInterfaceLabelWithDigit(model, t) })}
      onClose={onCancel}
      show={true}
      dialogType="activity"
      headerIcon="StillImage"
      content={content()}
      size="lg"
      className="responsive"
      buttons={[
        { label: t('general.cancel'), onClick: onCancel },
        {
          label: t('logos.button.update'),
          id: 'logo-configure-modal-submit',
          variant: 'primary',
          state: buttonState,
          onClick: (_e: any) => {
            submitAndValidate(formRef.current, t).then(() => {
              if (formRef.current?.isValid) {
                // success
                onCancel(); // close dialog
              }
            });
            return false; // prevent immediate close of modal
          },
          disabled: formRef.current?.isSubmitting || !anyChanges,
        },
      ]}
    />
  );
};
